import React from 'react';
import cn from 'classnames';

import type { IMessage } from 'widget/types/messages';
import MessageSectionMarker from 'widget/components/MessageSectionMarker';
import {
  formatDateForSeparator,
  shouldShowDateSeparator
} from 'widget/utils/formatDate';

import Message from './Message';
import EmptyMessages from './EmptyMessages';
import TypingMessage from './TypingMessage';
import styles from './MessageList.module.scss';

export type MessageListProps = {
  className?: string;
  messages?: IMessage[];
  onRetry?: (messageId: string) => void;
  isTyping?: boolean;
  botAvatarUrl?: string | null;
  hasMore?: boolean;
};

const MessageList: React.FC<MessageListProps> = ({
  className,
  messages = [],
  onRetry,
  isTyping,
  botAvatarUrl,
  hasMore = false
}) => {
  if (!messages.length) {
    return <EmptyMessages />;
  }

  return (
    <ul className={cn(styles['message-list'], className)}>
      {messages.map((message: IMessage, index) => {
        const prevMessage: IMessage = messages[index - 1];
        const nextMessage: IMessage = messages[index + 1];
        const showDateSeparator = shouldShowDateSeparator({
          currentDate: message.date,
          prevDate: prevMessage?.date,
          isFirstMessage: index === 0,
          hasMore
        });

        return (
          <React.Fragment key={message.id}>
            {showDateSeparator && (
              <li className={styles['date-separator']}>
                <MessageSectionMarker
                  title={formatDateForSeparator(message.date)}
                />
              </li>
            )}
            <li
              className={cn(
                styles['message'],
                message.isBot
                  ? styles['message_from-bot']
                  : styles['message_from-user']
              )}
            >
              <Message
                message={message}
                withAvatar={message.isBot && !nextMessage?.isBot}
                isFirst={!prevMessage || message.isBot !== prevMessage.isBot}
                onRetry={onRetry}
                botAvatarUrl={botAvatarUrl}
              />
            </li>
          </React.Fragment>
        );
      })}
      {isTyping && <TypingMessage className={styles['typing-message']} />}
    </ul>
  );
};

export default MessageList;
